/**
 * One metric bar per dossier tab, and one only.
 *
 * Every non-overview tab drew TWO bands of figures: the editable header strip
 * owned by `DealDossier` (Loan Purpose · Loan amount · Est. value · LTV · DSCR ·
 * Received · Priority · Program) and, immediately under it, whatever KPI strip
 * the page had built for itself. On Economics that meant Loan Amount, Est.
 * Value, LTV and DSCR were each printed twice, forty pixels apart, in two
 * different visual treatments — seventeen tiles saying thirteen things, and two
 * of the four repeats were even editable in one band and not the other.
 *
 * A page now PUBLISHES its metrics rather than drawing them. The bar merges
 * them onto the header slots by catalogue field name: a page metric that names
 * a field the header already carries keeps that slot's editability and required
 * marker while lending it the icon, the sponsor attribution and what it is
 * made of. Nothing is printed twice, and the bar's tiles are the same on
 * every tab (see HEADER_GROUPS).
 *
 * Each tile opens a detail dialog, because a figure on a dossier is only worth
 * as much as the answer to "where did that come from" — which the platform
 * holds (field lineage, the derivation rules, the sponsor's own cell) and never
 * showed anywhere near the number.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import { X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import type { SponsorFigure } from '@/services/dealFile'
import type { LineageVariance, LoanFieldLineageField } from '@/pages/pipeline-intelligence/types'
import { FIELD_STATE_TOKENS, type FieldState } from '@/components/fields/fieldState'
import { sponsorAttributionHolds, stateOfLineage } from '@/components/fields/lineage'
import { ValueProvenance } from '@/components/fields/ValueProvenance'
import { renderCatalogueInput, SlotLabel, SlotValueInput } from './ui'
import { DossierEditProvider, useDossierEdit, type DossierEdit } from './editContext'
import { catalogueValue, formatVarianceFigure } from './format'
import { isSystemField, isWritableField, slotKindOf } from './editableFields'
import type { SlotKind } from './editableFields'
import { fieldLabel, inputsFor, InputEditorList, outputFormulaOf } from './computedInputs'

/** One figure on the bar. */
export type DossierMetric = {
  /** Stable identity. The catalogue field name when the figure is one — that
   *  is what makes it editable, and what it dedupes on. */
  id: string
  label: string
  value: string
  icon?: LucideIcon
  kind?: SlotKind
  /** True when `id` is a real catalogue field, so the tile can be unlocked and
   *  edited in place rather than being a read-only restatement. */
  field?: boolean
  /** True when the figure shown IS catalogue field `id`'s own value, not a
   *  page computation that shares its name, so the value state (Derived,
   *  Suggested, Confirmed) applies. Unlike `field`, it adds no editing. */
  fieldValue?: boolean
  /** Set when the figure shown is the sponsor's own rather than one the client
   *  concluded. The distinction is the whole basis of the screening workflow. */
  sponsor?: SponsorFigure
  /** What the number is made of. Shown in the dialog. */
  detail?: { label: string; value: string }[]
  /** A short series behind the figure, drawn as bars in the dialog. */
  series?: { label: string; value: number | null }[]
  /** How to read the series values. */
  seriesFormat?: (value: number) => string
  /** One sentence on what the figure means or how it is arrived at. */
  note?: string
  /** The sponsor's model states this figure too, but the value on the deal
   *  is not theirs (a person confirmed it, or it was derived). */
  alsoInSponsorModel?: SponsorFigure
  /** A figure the page computes itself (not a catalogue field), named by its
   *  formula: the tile says Derived, as the page's own table does. */
  derived?: string
  /** A page figure that is not a catalogue field and is implied rather than
   *  recorded (a stack total from loan / LTV, the residual equity). The tile
   *  says so, as the table beside it does. */
  implied?: boolean
  /** The catalogue fields a page-computed figure (`derived`) is made of, so
   *  the unlocked detail dialog can offer them. Fields the engine derives name
   *  their inputs through the dossier (`inputsOf`) and need not set this. */
  inputs?: string[]
}

type Publish = (source: string, metrics: DossierMetric[]) => void

const MetricsContext = createContext<Publish | null>(null)

/**
 * Hold what the active page has published.
 *
 * Keyed by source so a tab switch replaces the previous page's metrics instead
 * of stacking on them — two pages are never mounted at once, but a page that
 * unmounts mid-transition must not leave its figures on the next tab's bar.
 */
export function DossierMetricsProvider({ children }: { children: ReactNode }) {
  const [published, setPublished] = useState<{ source: string; metrics: DossierMetric[] }>({
    source: '',
    metrics: [],
  })
  const publish = useCallback<Publish>((source, metrics) => {
    setPublished((prev) =>
      prev.source === source && sameMetrics(prev.metrics, metrics) ? prev : { source, metrics },
    )
  }, [])
  return (
    <MetricsContext.Provider value={publish}>
      <PublishedContext.Provider value={published.metrics}>{children}</PublishedContext.Provider>
    </MetricsContext.Provider>
  )
}

const PublishedContext = createContext<DossierMetric[]>([])

/** A page states its figures once, on every render, and the bar draws them. */
export function useProvideDossierMetrics(source: string, metrics: DossierMetric[]) {
  const publish = useContext(MetricsContext)
  useEffect(() => {
    publish?.(source, metrics)
    // A page that goes away takes its figures with it.
    return () => publish?.(source, [])
  }, [publish, source, metrics])
}

/** Cheap identity check — the metrics are rebuilt every render by value. */
function sameMetrics(a: DossierMetric[], b: DossierMetric[]): boolean {
  if (a.length !== b.length) return false
  return a.every(
    (metric, i) => metric.id === b[i].id &&
      metric.value === b[i].value &&
      metric.implied === b[i].implied &&
      metric.derived === b[i].derived,
  )
}

/**
 * The header, as the client's credit lead ruled it (Chris Young, 2 Nov): one
 * definition on every tab, each pair stacked in one tile, in this order —
 *   Initial Loan Amount over Fully Funded (total commitment) Loan Amount;
 *   LTV in-place over stabilized; Debt Yield in-place over stabilized;
 *   DSCR in-place over stabilized;
 * then Loan Purpose over Received and Priority over Program.
 *
 * NOI and Value (as-is) are not on it: "without the context of the loan
 * amount it isn't as important", and the value is only an input to LTV. They
 * stay on the Economics and Underwriting pages. A page no longer adds tiles of
 * its own (Economics drew Cash on Cash and IRR, the Capital Stack LTC, Cap
 * Rate and equity, Property NOI): the strip read differently on every tab.
 */
export const HEADER_GROUPS: { id: string; label: string; kind: SlotKind }[][] = [
  [
    // `loan_amount` is the initial advance where the deal records a split
    // (Calloway: $68M of a $71.12M commitment).
    { id: 'loan_amount', label: 'Initial Loan Amount', kind: 'currency' },
    { id: 'fully_funded_amount', label: 'Fully Funded Loan Amount', kind: 'currency' },
  ],
  [
    { id: 'ltv', label: 'LTV (In-Place)', kind: 'percent' },
    { id: 'ltv_stabilized', label: 'LTV (Stabilized)', kind: 'percent' },
  ],
  [
    { id: 'debt_yield', label: 'Debt Yield (In-Place)', kind: 'percent' },
    { id: 'debt_yield_stabilized', label: 'Debt Yield (Stabilized)', kind: 'percent' },
  ],
  [
    { id: 'dscr', label: 'DSCR (In-Place)', kind: 'number' },
    { id: 'dscr_stabilized', label: 'DSCR (Stabilized)', kind: 'number' },
  ],
  [
    { id: 'purpose', label: 'Loan Purpose', kind: 'text' },
    { id: 'originated', label: 'Received', kind: 'date' },
  ],
  [
    { id: 'priority', label: 'Priority', kind: 'text' },
    { id: 'capital_program', label: 'Program', kind: 'text' },
  ],
]

const HEADER_SLOTS = HEADER_GROUPS.flat()

/** Every catalogue field the bar shows, and so edits, on every tab. A page
 *  printing one of these says the bar is where it is edited (one input per
 *  field on a screen). */
export const HEADER_FIELDS: ReadonlySet<string> = new Set(HEADER_SLOTS.map((slot) => slot.id))

/**
 * Merge the page's figures onto the header's slots, in place, without repeats.
 *
 * A page lends a slot its own reading of the same field (its icon, sponsor
 * attribution, what the figure is made of); a page figure no slot names is
 * not drawn — the header is one definition on every tab. `derived` names the
 * formula of a header value the deal does not hold but the calculation
 * service computed, so it reads Derived as it does on Economics.
 *
 * Exported for its own test: the dedup is the whole point of this module and
 * it is much easier to state as a list of ids than to read out of a DOM.
 */
export function mergeMetrics(
  header: Record<string, string>,
  page: DossierMetric[],
  derived: Record<string, string> = {},
): DossierMetric[] {
  const byId = new Map(page.map((metric) => [metric.id, metric]))

  const slot = (spec: { id: string; label: string; kind: SlotKind }): DossierMetric => {
    const supplied = byId.get(spec.id)
    return {
      // The header slot's field name survives the merge, so the tile stays
      // editable and keeps its required marker; the page lends the rest.
      id: spec.id,
      field: true,
      kind: spec.kind,
      // The header's label, on every tab: a page's "LTV" or "DSCR" no longer
      // drops the in-place / stabilized axis the pair is read on.
      label: spec.label,
      value: supplied?.value ?? header[spec.id] ?? '—',
      icon: supplied?.icon,
      sponsor: supplied?.sponsor,
      detail: supplied?.detail,
      series: supplied?.series,
      seriesFormat: supplied?.seriesFormat,
      note: supplied?.note,
      // A page figure computed from stated ones, shown in a header slot the
      // deal leaves empty (the Capital Stack's DSCR, NOI ÷ debt service),
      // keeps its Derived / Implied mark in the slot: dropping it here made a
      // page's arithmetic read as a stated DSCR.
      // A header value the calculation service supplied says so too.
      derived: supplied ? supplied.derived : derived[spec.id],
      implied: supplied?.implied,
      fieldValue: supplied?.fieldValue,
      // And the inputs the page computed it from: dropped here, the slot's
      // dialog fell back to the catalogue's rule, which is not always the
      // arithmetic the page did.
      inputs: supplied?.inputs,
    }
  }

  // One rule for every slot, so none can ever be drawn twice or go missing.
  return HEADER_SLOTS.map(slot)
}

export function DossierMetricBar({
  header,
  derived,
  lineage,
  compact = false,
}: {
  compact?: boolean
  /** The header slots' own display values, from the catalogue. */
  header: Record<string, string>
  /** The formula of each header value the deal does not hold and the
   *  calculation service computed (it reads Derived). */
  derived?: Record<string, string>
  lineage: LoanFieldLineageField[]
}) {
  const page = useContext(PublishedContext)
  const lineageById = useMemo(() => {
    const map = new Map<string, LoanFieldLineageField>()
    for (const field of lineage) map.set(field.fieldName, field)
    return map
  }, [lineage])
  const metrics = useMemo(
    () =>
      mergeMetrics(header, page, derived).map((metric) =>
        metric.sponsor && !sponsorAttributionHolds(lineageById.get(metric.id), metric.sponsor)
          ? { ...metric, sponsor: undefined, alsoInSponsorModel: metric.sponsor }
          : metric,
      ),
    [header, page, derived, lineageById],
  )
  const [open, setOpen] = useState<DossierMetric | null>(null)
  const edit = useDossierEdit()
  const { unlocked } = edit
  const barRef = useRef<HTMLDivElement>(null)
  const { height: lockedHeight, measuring } = useLockedHeight(barRef, unlocked)
  // While re-measuring, the bar is laid out as it is locked, for one pass
  // before the browser paints (see useLockedHeight).
  const barEdit = useMemo(() => (measuring ? { ...edit, unlocked: false } : edit), [measuring, edit])

  return (
    <>
      <div
        ref={barRef}
        data-testid="dossier-metric-bar"
        // Unlocked, the bar keeps the height it had locked. A compact label
        // gives up its second line to a wider input (see CompactMetricRow),
        // so the strip neither grows nor shrinks and nothing below it moves.
        style={unlocked && !measuring && lockedHeight ? { minHeight: lockedHeight } : undefined}
        // Compact: as many columns as fit at a readable width, whole groups
        // wrapping onto another row of the same height. It used to be one
        // horizontally scrolling row of 120px groups, and at 1366 the Capital
        // Stack's eleventh group (Equity Multiple) was cut off (N3).
        className={compact ? "grid shrink-0 auto-rows-fr grid-cols-[repeat(auto-fit,minmax(8.5rem,1fr))] gap-1 border-b border-amber-400/10 px-3 py-1.5" : "grid flex-shrink-0 grid-cols-2 gap-1 border-b border-amber-400/10 px-3 py-1.5 sm:grid-cols-3 lg:grid-cols-6"}
      >
        <DossierEditProvider value={barEdit}>
        {compact ? <CompactMetrics metrics={metrics} lineageById={lineageById} onOpen={setOpen} /> : HEADER_GROUPS.map((group) => (
          // The same pairs as the compact bar, each stacked in one cell.
          <div key={group[0].id} data-metric-group={group[0].id} className="flex min-w-0 flex-col gap-1">
            {group.map(({ id }) => {
              const metric = metrics.find((item) => item.id === id)
              if (!metric) return null
              return (
                <MetricTile
                  key={metric.id}
                  metric={metric}
                  state={tileState(lineageById.get(metric.id), metric.value, Boolean(metric.field || metric.fieldValue))}
                  stateTitle={lineageById.get(metric.id)?.summary ?? null}
                  variance={metric.value !== '—' ? lineageById.get(metric.id)?.variance ?? null : null}
                  onOpen={() => setOpen(metric)}
                />
              )
            })}
          </div>
        ))}
        </DossierEditProvider>
      </div>
      {open && (
        <MetricDetailDialog
          metric={open}
          lineage={lineageById.get(open.id) ?? null}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  )
}

/** The element's height while the dossier is locked, kept current as the
 *  window resizes and held once it is unlocked.
 *
 *  The locked height depends on the width: at 1366 the Capital Stack strip
 *  is two rows (116px), at 1920 one (92px). Resized while unlocked, the bar
 *  used to keep the height it had at the old width, and locking it at the
 *  new one moved everything below by 24px (nd-061). So when the width
 *  changes while unlocked, `measuring` lays the bar out locked for one pass
 *  — measured and reverted in layout effects, before the browser paints —
 *  and the new locked height is held. */
function useLockedHeight(
  ref: RefObject<HTMLElement>,
  unlocked: boolean,
): { height: number | null; measuring: boolean } {
  const [height, setHeight] = useState<number | null>(null)
  const [measuring, setMeasuring] = useState(false)
  const widthRef = useRef<number | null>(null)
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    // Fractional, as laid out: a rounded height moved the page below by a pixel.
    const measure = () => {
      const box = element.getBoundingClientRect()
      widthRef.current = box.width
      setHeight(box.height || null)
    }
    if (!unlocked || measuring) {
      measure()
      if (measuring) {
        setMeasuring(false)
        return
      }
      if (typeof ResizeObserver === 'undefined') return
      const observer = new ResizeObserver(measure)
      observer.observe(element)
      return () => observer.disconnect()
    }
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      const width = element.getBoundingClientRect().width
      if (widthRef.current !== null && Math.abs(width - widthRef.current) > 0.5) setMeasuring(true)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref, unlocked, measuring])
  return { height, measuring }
}

/** The catalogue kind of the field a tile shows, so a figure about it is
 *  printed with its unit: the catalogue's own entry first, the tile's slot
 *  kind otherwise. */
function metricKind(edit: DossierEdit, metric: DossierMetric): string | undefined {
  return edit.defs.find((d) => d.name === metric.id)?.kind ?? metric.kind
}

/** A tile's variance mark, on the scale the server states it in: a percent
 *  is points, so Calloway's computed 0.739 cash-on-cash reads 0.74%, not the
 *  raw float 0.7387755102040816 with no unit. */
function varianceTitle(variance: LineageVariance, kind: string | undefined): string {
  return `Variance, for information: stated ${formatVarianceFigure(variance.stated, kind)}; the deal's other figures give ${formatVarianceFigure(variance.computed, kind)}${variance.formula ? ` (${variance.formula})` : ''}.`
}

/** The state word a compact tile prints under its label: Derived, Suggested
 *  or Confirmed (a stated figure's word is whose it is, when that is the
 *  sponsor), and Variance where the deal's own figures disagree with it. */
function CompactTileMarks({ metric, lineage }: { metric: DossierMetric; lineage?: LoanFieldLineageField }) {
  const edit = useDossierEdit()
  const state: FieldState | null = edit.isDerivedField(metric.id) || shownPageDerived(edit, metric)
    ? 'derived'
    : tileState(lineage, metric.value, Boolean(metric.field || metric.fieldValue))
  const shown =
    state === 'derived' || state === 'suggested' || state === 'confirmed' || state === 'in_unread_model' ? state : null
  const variance = metric.value !== '—' ? lineage?.variance ?? null : null
  // A page's own computation names its own formula; a field's value, the
  // lineage's.
  const formula = shownPageDerived(edit, metric) ? metric.derived : lineage?.derivation?.formula
  return (
    <>
      {metric.sponsor && <span className="ml-1 inline-block text-[7px] text-sky-300/80">Sponsor</span>}
      {!metric.sponsor && shown ? (
        <span
          data-tile-state={shown}
          title={
            shown === 'derived' && formula
              ? `Derived: ${formula}`
              : shown === 'in_unread_model'
                ? lineage?.summary ?? FIELD_STATE_TOKENS[shown].description
                : undefined
          }
          className={`ml-1 inline-block text-[7px] ${shown === 'derived' ? 'text-violet-300/80' : shown === 'confirmed' ? 'text-emerald-300/80' : shown === 'in_unread_model' ? 'text-indigo-300/80' : 'text-stone-400'}`}
        >
          {FIELD_STATE_TOKENS[shown].label}
        </span>
      ) : null}
      {/* A total built on loan ÷ LTV (or its residual equity) is implied, not
          recorded: the mark rides beside its Derived state, as the table says. */}
      {!metric.sponsor && shownImplied(edit, metric) ? (
        <span data-tile-implied className="ml-1 inline-block text-[7px] text-sky-300/80">
          Implied
        </span>
      ) : null}
      {variance ? (
        <span
          data-tile-variance
          title={varianceTitle(variance, metricKind(edit, metric))}
          className="ml-1 inline-block text-[7px] text-amber-300/80"
        >
          Variance
        </span>
      ) : null}
    </>
  )
}

function CompactMetrics({
  metrics,
  lineageById,
  onOpen,
}: {
  metrics: DossierMetric[]
  lineageById: Map<string, LoanFieldLineageField>
  onOpen: (metric: DossierMetric) => void
}) {
  const groups = HEADER_GROUPS.map((group) => group.map((slot) => slot.id))
  return <>{groups.filter(ids => ids.some(id => metrics.some(metric => metric.id === id))).map((ids) => <div key={ids[0]} data-metric-group={ids[0]} className="flex min-w-0 flex-col justify-center gap-1 rounded-md border border-amber-400/10 bg-black/40 px-2 py-1.5">
    {ids.map(id => { const metric = metrics.find(item => item.id === id); if (!metric) return null
      return <CompactMetricRow key={id} metric={metric} lineage={lineageById.get(id)} onOpen={() => onOpen(metric)} />
    })}
  </div>)}</>
}

/** The agreed state of the figure a tile shows, when it names a field that
 *  holds a value. A page figure that is not itself the field (`isField`
 *  false) may be computed on the page, so it takes no state of its own — but
 *  when it is blank and the deal's field of the same name is blank too, the
 *  unread-model mark is still true of it. */
function tileState(lineage: LoanFieldLineageField | undefined, value: string, isField: boolean): FieldState | null {
  if (!lineage) return null
  const state = stateOfLineage(lineage)
  // A blank an attached, unread model may fill says so — a bare "—" beside
  // the Underwriting tab's "In unread model" count read as "nothing states it".
  if (value === '—' || value === '') return state === 'in_unread_model' ? state : null
  if (!isField) return null
  return state === 'missing' || state === 'not_applicable' ? null : state
}

/** A page's own computed figure, when it holds a value. */
function pageDerived(metric: DossierMetric): boolean {
  return Boolean(metric.derived) && metric.value !== '—' && metric.value !== ''
}

/**
 * The tile's own field holds a person's unsaved edit. The page recomputes its
 * figures from the draft on every keystroke, and a cleared DSCR — or the "0"
 * on the way to 0.95 — is no figure at all, so the Capital Stack fell back to
 * NOI ÷ debt service and published it Derived. The tile turned into a button
 * under the cursor and the stated DSCR could not be typed back. While the
 * field is in the draft, the tile is the input being typed into.
 *
 * Only while unlocked, and only for an entry that changes something. A digit
 * typed under Audit and deleted leaves a blank over a blank in the draft:
 * Save drops it as unchanged and locking does not clear it, so the locked bar
 * printed the page's NOI ÷ debt service with no Derived mark, as a stated
 * DSCR; unlocked, an empty box hid it.
 */
function typedOver(edit: DossierEdit, metric: DossierMetric): boolean {
  if (!edit.unlocked || !edit.draft || !(metric.id in edit.draft)) return false
  return !(isBlank(edit.draft[metric.id]) && isBlank(catalogueValue(edit.raw, edit.defs, metric.id)))
}

/** Nothing typed, or nothing held. */
function isBlank(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
}

/** A page figure the tile marks Derived: computed by the page, and not the
 *  input a person is typing over (that one is theirs until saved). */
function shownPageDerived(edit: DossierEdit, metric: DossierMetric): boolean {
  return pageDerived(metric) && !isEditing(edit, metric)
}

/** The Implied mark, on the same terms: a stated equity cleared to retype it
 *  falls back to the page's residual, and the box being typed into read
 *  "Implied Equity · Implied". */
function shownImplied(edit: DossierEdit, metric: DossierMetric): boolean {
  return Boolean(metric.implied) && !isEditing(edit, metric)
}

/** The tile's label: the page's, except over an input that holds the
 *  person's figure where the page's label names its implied fallback — then
 *  the catalogue's name for the field being typed. */
function tileLabel(edit: DossierEdit, metric: DossierMetric): string {
  return metric.implied && isEditing(edit, metric) ? fieldLabel(edit, metric.id) : metric.label
}

/**
 * A figure on the bar that is computed rather than held: one the engine
 * derives (lineage Derived — R1 forbids typing over it, and the server refuses
 * the write with a 422), one the page works out itself or implies (the
 * Capital Stack's residual equity), or a page figure standing in for a field
 * that holds nothing (Economics' NOI from the calculation service while the
 * deal states none — an input over it would show an empty box where the
 * figure was). Never a field a person is typing over.
 */
function isComputedTile(edit: DossierEdit, metric: DossierMetric): boolean {
  if (typedOver(edit, metric)) return false
  if (outputFormulaOf(edit, metric.id) !== null) return true
  if (pageDerived(metric)) return true
  // Implied is computed too: the Implied Equity tile was neither an input
  // nor a computed tile, so unlocking left it inert.
  if (metric.implied && metric.value !== '—' && metric.value !== '') return true
  const held = edit.effectiveValue(metric.id)
  const holds = held !== null && held !== undefined && held !== ''
  return Boolean(metric.fieldValue) && !metric.field && !holds && metric.value !== '—' && metric.value !== ''
}

/**
 * A catalogue figure on the bar is an input while the dossier is unlocked, if
 * a save can write it and it is the field's own value; every other figure
 * reads as it does locked. `fieldValue` figures (the page's NOI, LTC, IRR
 * tiles) are fields too: they were read-only here and editable only under
 * Audit, which is the inefficiency the owner asked to remove (21 Oct). So is
 * a field being typed over: a stated LTC cleared to retype it is no longer
 * the page's `fieldValue`, and the tile must not drop its input for that.
 */
function isEditing(edit: DossierEdit, metric: DossierMetric): boolean {
  if (!edit.unlocked || !(metric.field || metric.fieldValue || typedOver(edit, metric))) return false
  if (isSystemField(metric.id) || !isWritableField(metric.id, edit.defs)) return false
  return !isComputedTile(edit, metric)
}

/** The input kind for a tile: the page's, else the catalogue entry's. */
function tileKind(edit: DossierEdit, metric: DossierMetric): SlotKind {
  return metric.kind ?? slotKindOf(edit.defs.find((d) => d.name === metric.id))
}

/**
 * The inputs a computed tile offers: the page's own list when it names one,
 * else the dossier's (the lineage, then the catalogue's rules). An implied
 * figure is the page's residual arithmetic, never an engine rule, so it
 * offers only what the page names: the catalogue's total_equity rule (total
 * cost − loan) offered Total Project Cost on an equity implied from the
 * purchase price, and a price typed there is a wrong project cost.
 */
function tileInputs(edit: DossierEdit, metric: DossierMetric): string[] {
  if (metric.implied && !metric.inputs) return []
  return inputsFor(edit, metric.id, metric.inputs)
}

/** What an unlocked computed tile says on hover: it opens its inputs. */
function computedTileTitle(edit: DossierEdit, metric: DossierMetric): string | null {
  if (!edit.unlocked || !isComputedTile(edit, metric)) return null
  const inputs = tileInputs(edit, metric)
  return inputs.length
    ? `${metric.label}: computed — open to edit its inputs (${inputs.map((name) => fieldLabel(edit, name)).join(', ')})`
    : `${metric.label}: computed — correct the figures it is made of`
}

/**
 * One figure of the compact bar: its label line, then its value line. Unlocked,
 * the row is the same row — same element sizes, same type, same state marks —
 * with the value drawn as an input (see `.dossier-edit-input`, exactly one line
 * tall); the group box around it does not change.
 *
 * The label has a line of its own in both modes. Side by side with the value,
 * the unlocked input (wider than the "$68M" it replaces) squeezed the label
 * to "LOAN…" at 1366, and read mode wrapped long labels over their values
 * (N3). A required field's " *" is reserved, invisible, in read mode, so a
 * label wraps the same way locked and unlocked.
 */
/** A value typed as words (a purpose, a program), which may wrap. */
function isTextKind(kind: SlotKind | undefined): boolean {
  return kind === undefined || kind === 'text' || kind === 'select'
}

function CompactMetricRow({
  metric,
  lineage,
  onOpen,
}: {
  metric: DossierMetric
  lineage?: LoanFieldLineageField
  onOpen: () => void
}) {
  const edit = useDossierEdit()
  const editing = isEditing(edit, metric)
  const marks = <CompactTileMarks metric={metric} lineage={lineage} />
  const labelLine = 'min-w-0 break-words font-mono text-[8px] uppercase tracking-wide text-stone-500'
  // One grid cell holds the value text and, unlocked, the input laid over it:
  // a long value ("Value-Add Acquisition") wraps rather than being cut off,
  // and the line keeps the height its text gives it in both modes.
  const valueLine =
    'relative grid min-w-0 font-mono text-[11px] font-semibold text-stone-100 [&>*]:[grid-area:1/1] [&>*]:self-start [&>.contents>input]:[grid-area:1/1] [&>.contents>input]:self-start [&>.contents>textarea]:[grid-area:1/1]'
  if (editing) {
    const label = tileLabel(edit, metric)
    return (
      <label title={label} className="flex min-w-0 flex-col gap-0.5 text-left">
        <span data-compact-label className={labelLine}><SlotLabel name={metric.id} label={label} />{marks}</span>
        <span data-compact-value className={valueLine}>
          <span aria-hidden="true" className="invisible min-w-0 break-words">{metric.value}</span>
          {/* Text wraps over the lines its read-only value takes, so a long
              value is shown whole, not cut at one line (nd-064). */}
          <SlotValueInput name={metric.id} label={label} kind={tileKind(edit, metric)} multiline={isTextKind(tileKind(edit, metric))} />
        </span>
      </label>
    )
  }
  const reserveRequired =
    Boolean(metric.field) && isWritableField(metric.id, edit.defs) && edit.defs.some((d) => d.name === metric.id && d.required)
  return (
    <button type="button" onClick={onOpen} title={computedTileTitle(edit, metric) ?? `${metric.label}: ${metric.value} — source details`} className="flex min-w-0 flex-col gap-0.5 text-left hover:text-amber-200">
      <span data-compact-label className={labelLine}>
        {metric.label}
        {reserveRequired ? <span aria-hidden="true" className="invisible"> *</span> : null}
        {marks}
      </span>
      <span data-compact-value className={valueLine}>
        <span className="min-w-0 break-words">{metric.value}</span>
      </span>
    </button>
  )
}

function MetricTile({
  metric,
  state,
  stateTitle = null,
  variance = null,
  onOpen,
}: {
  metric: DossierMetric
  state?: FieldState | null
  /** The lineage's one-line source, shown on hover of an unread-model mark. */
  stateTitle?: string | null
  /** A stated figure the deal's other figures do not reproduce (rule R4).
   *  Marked on the tile, for information — Calloway's cash-on-cash read
   *  4.10% with no sign the engine computes 0.74% (ECON-25). */
  variance?: LineageVariance | null
  onOpen: () => void
}) {
  const edit = useDossierEdit()
  const shownState: FieldState | null = edit.isDerivedField(metric.id) || shownPageDerived(edit, metric) ? 'derived' : state ?? null
  // Unlocked, the tile keeps its box, icon, label and state marks, and its
  // value becomes an input in the place the figure was drawn. It is a label
  // rather than a button then: an expand control laid over the input would
  // compete with the caret for the same click.
  const editing = isEditing(edit, metric)
  const label = tileLabel(edit, metric)
  const Shell = editing ? 'label' : 'button'
  return (
    <Shell
      {...(editing ? {} : { type: 'button' as const, onClick: onOpen })}
      title={editing ? label : computedTileTitle(edit, metric) ?? `${metric.label} — where this figure came from`}
      className={`group flex h-full min-h-[46px] min-w-0 items-center gap-2 rounded-md border border-amber-400/10 bg-black/40 px-2 py-1.5 text-left transition-colors ${
        editing ? '' : 'hover:border-amber-300/35 hover:bg-amber-300/[0.05]'
      }`}
    >
      {metric.icon ? (
        <span
          className={
            metric.sponsor
              ? 'flex h-7 w-7 shrink-0 items-center justify-center rounded border border-sky-400/15 bg-sky-400/[0.07] text-sky-300/80'
              : 'flex h-7 w-7 shrink-0 items-center justify-center rounded border border-amber-300/10 bg-amber-300/[0.05] text-amber-300/80'
          }
        >
          <metric.icon size={13} />
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1">
          <span className="truncate font-mono text-[8px] uppercase tracking-[0.14em] text-stone-600">
            {editing ? <SlotLabel name={metric.id} label={label} /> : metric.label}
          </span>
          {/* The client screens on the sponsor's pro forma and underwrites
              after — only safe while the number says whose it is. */}
          {metric.sponsor ? (
            <span className="shrink-0 font-mono text-[7px] uppercase tracking-[0.14em] text-sky-300/80">
              Sponsor
            </span>
          ) : shownState ? (
            // The figure's own state, on the tile: a derived value says so
            // before anyone opens it (OVR-05, UW-04).
            <span
              data-tile-state={shownState}
              title={
                shownState === 'derived' && shownPageDerived(edit, metric)
                  ? `Derived: ${metric.derived}`
                  : shownState === 'in_unread_model'
                    ? stateTitle ?? FIELD_STATE_TOKENS[shownState].description
                    : undefined
              }
              className={`shrink-0 font-mono text-[7px] uppercase tracking-[0.14em] ${
                shownState === 'derived'
                  ? 'text-violet-300/80'
                  : shownState === 'suggested'
                    ? 'text-stone-400'
                    : shownState === 'confirmed'
                      ? 'text-emerald-300/80'
                      : shownState === 'in_unread_model'
                        ? 'text-indigo-300/80'
                        : 'text-sky-300/60'
              }`}
            >
              {FIELD_STATE_TOKENS[shownState].label}
            </span>
          ) : null}
          {/* Implied (a total on loan ÷ LTV, its residual equity) is a mark
              beside the figure's state, not a state of its own. */}
          {!metric.sponsor && shownImplied(edit, metric) ? (
            <span
              data-tile-implied
              className="shrink-0 font-mono text-[7px] uppercase tracking-[0.14em] text-sky-300/80"
            >
              Implied
            </span>
          ) : null}
          {variance ? (
            <span
              data-tile-variance
              title={varianceTitle(variance, metricKind(edit, metric))}
              className="shrink-0 font-mono text-[7px] uppercase tracking-[0.14em] text-amber-300/80"
            >
              Variance
            </span>
          ) : null}
        </span>
        <span
          className={`block ${editing ? '' : 'truncate'} font-mono text-[14px] font-semibold leading-tight ${
            edit.isDerivedField(metric.id) ? 'text-violet-300' : 'text-stone-100'
          }`}
        >
          {editing ? <SlotValueInput name={metric.id} label={label} kind={tileKind(edit, metric)} /> : metric.value}
        </span>
      </span>
    </Shell>
  )
}

/**
 * Where one figure came from.
 *
 * The dossier holds the answer in three separate places — field lineage (who
 * wrote it, from what source, at what confidence, and every value it held
 * before), the derivation rules (which inputs produce it), and the sponsor's
 * workbook cell — and showed it on three separate screens, none of them the one
 * with the number on it.
 */
function MetricDetailDialog({
  metric,
  lineage,
  onClose,
}: {
  metric: DossierMetric
  lineage: LoanFieldLineageField | null
  onClose: () => void
}) {
  const edit = useDossierEdit()
  // Same reason as the panel modal: the dossier shell closes the whole record
  // on Escape, so this one has to be taken before it bubbles that far.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])
  const derived = edit.isDerivedField(metric.id)
  const editableInputs = edit.unlocked && isComputedTile(edit, metric) ? tileInputs(edit, metric) : []
  const history = (lineage?.history ?? []).slice(0, 12)
  const seriesMax = Math.max(
    1,
    ...(metric.series ?? []).map((point) => Math.abs(point.value ?? 0)),
  )
  const format = metric.seriesFormat ?? ((value: number) => String(value))

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${metric.label} detail`}
      onClick={onClose}
    >
      <div
        className="flex max-h-[86vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-amber-400/20 bg-[#0b0b0b] shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-amber-400/10 px-4 py-3">
          <div className="min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-amber-300/90">
              {metric.label}
            </div>
            <div
              className={`mt-0.5 font-mono text-[24px] font-semibold leading-none ${
                derived ? 'text-violet-300' : 'text-stone-50'
              }`}
            >
              {metric.value}
            </div>
            {metric.note && (
              <p className="mt-1.5 text-[11px] leading-relaxed text-stone-400">{metric.note}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 text-stone-500 hover:text-stone-200"
          >
            <X size={14} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
          <Section title="Where this came from">
            {lineage && !metric.sponsor ? (
              <div className="space-y-2">
                <ValueProvenance field={lineage} kind={metricKind(edit, metric)} state={derived ? 'derived' : undefined} />
                {metric.alsoInSponsorModel ? (
                  <p className="text-[10px] text-stone-500">
                    The sponsor&rsquo;s model also states this figure, at{' '}
                    {metric.alsoInSponsorModel.filename} {metric.alsoInSponsorModel.sheet}!
                    {metric.alsoInSponsorModel.cell}. The value on the deal is not theirs.
                  </p>
                ) : null}
              </div>
            ) : metric.sponsor ? (
              <div className="space-y-1">
                <Row label="Stated by" value="The sponsor, not the client" />
                <Row label="Workbook" value={metric.sponsor.filename} />
                <Row
                  label="Cell"
                  value={`${metric.sponsor.sheet}!${metric.sponsor.cell}${
                    metric.sponsor.computed ? ' (a formula, not a typed input)' : ''
                  }`}
                />
                {metric.sponsor.label && <Row label="Their label" value={metric.sponsor.label} />}
                {/* A workbook stating the column twice is stating two things,
                    and neither is safe to silently prefer. */}
                {metric.sponsor.alternatives && metric.sponsor.alternatives.length > 0 && (
                  <Row
                    label="Also stated at"
                    value={metric.sponsor.alternatives
                      .map((alt) => `${alt.sheet}!${alt.cell}`)
                      .join(', ')}
                  />
                )}
              </div>
            ) : derived ? (
              <div className="space-y-1">
                <Row label="State" value="Derived — recalculated from your unsaved edits" />
                <Row label="Field" value={metric.id} />
              </div>
            ) : (
              // No lineage row for this figure: one the page computes from
              // other fields, or a record not yet loaded. Said plainly — and
              // as a suggestion, never as an unsourced extraction.
              <p className="text-[11px] text-stone-500">
                No source is on file for this figure. Treat it as a suggestion until a person
                confirms it{metric.detail?.length ? ' — what it is made of is listed below' : ''}.
              </p>
            )}
          </Section>

          {/* Unlocked, a computed figure is corrected where it is read: its
              inputs, each the dossier's own input, writing the same draft as
              every other edit. The figure itself is never an input (R1). */}
          {editableInputs.length > 0 && (
            <Section title="Edit its inputs">
              <p className="mb-1.5 text-[10px] leading-snug text-stone-500">
                This figure is computed, so it is not typed over. Correct what it is made of; it
                recomputes from them, and the dossier&rsquo;s Save records the change.
              </p>
              <InputEditorList names={editableInputs} renderInput={renderCatalogueInput} />
            </Section>
          )}

          {metric.detail && metric.detail.length > 0 && (
            <Section title="What it is made of">
              <div className="space-y-1">
                {metric.detail.map((row) => (
                  <Row key={row.label} label={row.label} value={row.value} />
                ))}
              </div>
            </Section>
          )}

          {metric.series && metric.series.length > 0 && (
            <Section title="Over time">
              <div className="flex items-end gap-1.5" style={{ height: 132 }}>
                {metric.series.map((point) => {
                  const height =
                    point.value == null ? 0 : (Math.abs(point.value) / seriesMax) * 100
                  return (
                    <div
                      key={point.label}
                      className="flex min-w-0 flex-1 flex-col items-center justify-end gap-1"
                      title={point.value == null ? 'Not recorded' : format(point.value)}
                    >
                      <span className="font-mono text-[8px] text-stone-500">
                        {point.value == null ? '—' : format(point.value)}
                      </span>
                      <div
                        className="w-full rounded-sm bg-amber-300/40"
                        style={{ height: `${Math.max(height, point.value == null ? 0 : 2)}%` }}
                      />
                      <span className="w-full truncate text-center font-mono text-[8px] text-stone-600">
                        {point.label}
                      </span>
                    </div>
                  )
                })}
              </div>
            </Section>
          )}

          {history.length > 0 && (
            <Section title={`Every value it has held (${lineage?.history.length ?? 0})`}>
              <div className="space-y-1">
                {history.map((event) => (
                  <div
                    key={event.id}
                    className="flex items-baseline justify-between gap-3 border-b border-white/[0.04] py-1 last:border-b-0 text-[11px]"
                  >
                    <span className="min-w-0 flex-1 truncate text-stone-400">
                      {displayOf(event.oldValue)} → <span className="text-stone-200">{displayOf(event.newValue)}</span>
                    </span>
                    <span className="shrink-0 font-mono text-[9px] text-stone-600">
                      {event.source ?? event.valueState} · {whenText(event.createdAt)}
                    </span>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <div className="mb-1.5 flex items-center gap-2">
        <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-stone-600">
          {title}
        </span>
        <span className="h-px flex-1 bg-amber-400/10" />
      </div>
      {children}
    </section>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[11px]">
      <span className="shrink-0 text-stone-500">{label}</span>
      <span className="min-w-0 break-words text-right font-mono text-stone-200">{value}</span>
    </div>
  )
}

function displayOf(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  return String(value)
}

function whenText(iso: string | null | undefined): string {
  if (!iso) return '—'
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
